import React, { useContext, useEffect, useState } from 'react';
import { InputField } from '@trp-beacon-ui-kit/react-controls';
import { FlowContext } from '../../core/flow-engine';
import styles from './style.css';
import {
  getHeaderText,
  getURLTickers,
  redirectRolloverNoAuth,
  renderPrimaryEmailTitleWithTooltip,
  renderDobTitleWithTooltip,
  renderAmountTitleWithTooltip
} from './util';
import { withDefault } from '../../core/util';
import PhoneNumber from '../../components/PhoneNumber/PhoneNumber';
import Heading from '../../components/Typography/Heading/Heading';
import LoginCTA from '../../components/LoginCTA/LoginCTA';
import ContributionLimits from '../../components/ContributionLimits/ContributionLimits';
import MaxedOutContributionsModal from '../../components/MaxedOutContributionsModal/MaxedOutContributionsModal';
import ProfileScreenButtons from '../../components/Button/ProfileScreenButtons';
import {
  firstnameValidation,
  lastnameValidation,
  restrictedFirstNameValidation,
  restrictedLastNameValidation
} from '../../flows/shared/validations';
import { handleInputFormat } from '../../flows/shared/utils';
import { setMaxedContributoryLimit } from '../../flows/contributory/util';
import { generateGeneralTestID } from '../../generateAutomationTestIDs';
import ExistingAgreementsBlockModal from '../../components/ExistingAgreementModals/ExistingAgreementsBlockModal';

// TODO [2021-10-19]: reduce complexity - reference issue #7
/* eslint-disable complexity */
const ContributoryProfileScreen = () => {
  const { back, next, formProps, flowProps, appState } = useContext(
    FlowContext
  );
  const { store } = appState;
  const {
    rpsDirectRollover,
    authenticated,
    enabled,
    prospect,
    app,
    amount,
    accountType,
    priorYearAmount,
    currentYearAmount,
    loadedData,
    existingAgreements
  } = store;
  const { customer, fromIRAAccountType, iraAccountType } = app;

  const showContributionLimits =
    loadedData?.contributionPeriod && loadedData?.contributionLimits;

  useEffect(() => {
    redirectRolloverNoAuth(rpsDirectRollover, authenticated);
  }, [rpsDirectRollover, authenticated]);

  // these will exist if user is authenticated or a RJ user
  const firstName = customer?.name?.firstName;
  const lastName = customer?.name?.lastName;
  const primaryEmailAddress = customer?.primaryEmailAddress;
  const dob = customer?.dob;

  const disableFlag = authenticated && enabled;

  // multi-fund buy query check
  // capture tickers to pass to InvestmentReviewScreen
  const multiBuyTickers = getURLTickers(window.location.search);

  // contributory logic start
  const [dobState, changeDobState] = useState(dob);
  const handleChangeDobState = event => {
    if (event.target.value.length !== 10) return;
    changeDobState(event.target.value);
  };

  const [maxedOut, setMaxedOutLimits] = useState(false);
  const [priorYearState, setPriorYear] = useState(priorYearAmount || '');
  const [currentYearState, setCurrentYear] = useState(currentYearAmount || '');
  const [continueClicked, setContinueClicked] = useState(false);
  const [displayAmountError, setDisplayAmountError] = useState(false);

  const [dobValue, setDobValue] = useState(withDefault(dob, ''));
  const [amountValue, setAmountValue] = useState(withDefault(amount, ''));

  // determines if customer has already maxed out contribution limits
  // sets flag to display modal if they have
  useEffect(() => {
    if (enabled) {
      return setMaxedContributoryLimit(loadedData, setMaxedOutLimits);
    }

    return setMaxedOutLimits(false);
  }, []);

  useEffect(() => {
    return continueClicked && !priorYearState && !currentYearState
      ? setDisplayAmountError(true)
      : setDisplayAmountError(false);
  }, [priorYearState]);

  useEffect(() => {
    return continueClicked && !priorYearState && !currentYearState
      ? setDisplayAmountError(true)
      : setDisplayAmountError(false);
  }, [currentYearState]);

  const handleContinueClick = () => {
    setContinueClicked(true);
    // if no errors, call next()
    if (priorYearState === '' && currentYearState === '') {
      setDisplayAmountError(true);
      return next('runValidation')();
    }
    return next(multiBuyTickers)();
  };
  // contributory logic end

  // US661619 (COMPLIANCE) - block clients opening agreements that match an existing agreement
  const hasExistingAgree = existingAgreements && existingAgreements?.length > 0;

  return (
    <div className="profile-fields">
      <Heading
        text={getHeaderText(
          authenticated,
          firstName,
          accountType,
          fromIRAAccountType,
          iraAccountType
        )}
      />

      <LoginCTA authenticated={authenticated} />

      <div className="name-fields">
        <div data-e2e="firstname" className="name-column">
          <InputField
            type="text"
            title="First Name"
            {...formProps.firstName}
            disabled={disableFlag}
            defaultValue={firstName}
            maxLength="35"
            validation={
              disableFlag ? firstnameValidation : restrictedFirstNameValidation
            }
            className="blocked_data"
            data-name={generateGeneralTestID(
              'BasicPI',
              'AccountOwnerFirstName'
            )}
          />
        </div>
        <div data-e2e="lastname" className="name-column">
          <InputField
            type="text"
            title="Last Name"
            {...formProps.lastName}
            disabled={disableFlag}
            defaultValue={lastName}
            maxLength="35"
            validation={
              disableFlag ? lastnameValidation : restrictedLastNameValidation
            }
            className="blocked_data"
            data-name={generateGeneralTestID('BasicPI', 'AccountOwnerLastName')}
          />
        </div>
      </div>
      <div data-e2e="email">
        <InputField
          type="email"
          title={renderPrimaryEmailTitleWithTooltip()}
          {...formProps.primaryEmailAddress}
          defaultValue={primaryEmailAddress}
          disabled={
            prospect
          } /* disable email fields for prospect due to email verification */
          className="blocked_data"
          data-name={generateGeneralTestID('BasicPI', 'AccountOwnerEmail')}
        />
      </div>
      <div data-e2e="dateofbirth">
        <InputField
          title={renderDobTitleWithTooltip()}
          aria-describedby="hint"
          type="text"
          placeholder="mm/dd/yyyy"
          maxLength="10"
          {...formProps.dob}
          disabled={enabled}
          value={dobValue}
          onChange={e => {
            handleChangeDobState(e);
            handleInputFormat(e, 'DATE', setDobValue);
          }}
          className="blocked_data"
          data-name={generateGeneralTestID('BasicPI', 'AccountOwnerBirthDate')}
        />
        <dd id="hint" style={{ display: 'none' }}>
          This is a required field. Please enter a two digit month, a two digit
          day and a four digit year for the Date of Birth value.
        </dd>
      </div>

      {showContributionLimits ? (
        <ContributionLimits
          dob={dobState}
          priorYearState={priorYearState}
          setPriorYear={setPriorYear}
          currentYearState={currentYearState}
          setCurrentYear={setCurrentYear}
          displayAmountError={displayAmountError}
        />
      ) : (
        <div data-e2e="amount">
          <InputField
            onChange={evt =>
              handleInputFormat(evt, 'DECIMAL_CURRENCY', setAmountValue)
            }
            maxLength="18"
            disabled={rpsDirectRollover}
            title={renderAmountTitleWithTooltip()}
            {...formProps.amount}
            value={amountValue}
            data-name={generateGeneralTestID('BasicPI', 'InvestmentAmount')}
          />
        </div>
      )}

      <ProfileScreenButtons
        multiBuyTickers={multiBuyTickers}
        handleContinueClick={handleContinueClick}
        {...(!flowProps?.isReadyChoice && { handleBackClick: back('landing') })}
      />

      <PhoneNumber screen="default" />
      <style jsx>{styles}</style>

      {maxedOut && <MaxedOutContributionsModal appState={appState} />}
      {/* block account opening if maxed out fals is false and existing agreement flag is true */}
      {!maxedOut && hasExistingAgree && <ExistingAgreementsBlockModal />}
    </div>
  );
};
/* eslint-enable complexity */
export default ContributoryProfileScreen;
